import {useProductDetails} from "../../store/productDetails";
import {RatingDisplay} from "../common/RatingDisplay";
import {useEffect, useState} from "@wordpress/element";
import {useCompare} from "../../store/compare";
import {AddToCart as AddToCartButton} from "../common/AddToCart";
import {SourceLogo} from "../common/SourceLogo";
import {useMPE} from "../../hooks/useMPE";

const BodySkeleton = () => (
    <div className="animate-pulse">
        <div className="flex gap-2 mb-4">
            <div className="h-6 w-16 bg-base-300 rounded"/>
        </div>
        <div className="h-4 bg-base-300 rounded w-1/4 mb-2"/>
        <div className="h-8 bg-base-300 rounded w-3/4 mb-2"/>
        <div className="h-8 bg-base-300 rounded w-1/2 mb-8"/>

        <div className="space-y-3 mb-8">
            <div className="h-4 bg-base-300 rounded w-full"/>
            <div className="h-4 bg-base-300 rounded w-5/6"/>
            <div className="h-4 bg-base-300 rounded w-4/6"/>
        </div>

        <div className="flex gap-1 mb-8">
            {Array.from({length: 5}).map((_, i) => (
                <div key={i} className="w-4 h-4 bg-base-300 rounded"/>
            ))}
            <div className="h-4 w-10 bg-base-300 rounded ml-2"/>
        </div>

        <div className="mb-10">
            <div className="h-12 bg-base-300 rounded w-40 mb-2"/>
            <div className="h-4 bg-base-300 rounded w-24 mb-6"/>
            <div className="h-4 bg-base-300 rounded w-48 mb-8"/>
            <div className="max-w-xs space-y-4">
                <div className="flex gap-4">
                    <div className="h-12 w-32 bg-base-300 rounded-full"/>
                    <div className="h-12 flex-1 bg-base-300 rounded-full"/>
                </div>
                <div className="h-12 w-full bg-base-300 rounded-full"/>
            </div>
        </div>
    </div>
);

export const ProductDetailsBody = () => {
    const {
        product: d,
        loadProduct,
        loading
    } = useProductDetails();

    const {initMpe} = useMPE();
    const [qty, setQty] = useState(1);
    const {add: addCompare, remove: removeCompare, contains} = useCompare();
    const inCompare = contains(String(d.sku));

    const comparePayload = d ? {
        sku: d.sku,
        data: d.data,
        images: d.images,
        source: d.source || 'BEST_BUY'
    } : null;

    const handleToggleCompareStatus = () => {
        if (inCompare) {
            removeCompare(String(d.sku))
        } else {
            comparePayload && addCompare(comparePayload)
        }
    };

    useEffect(() => {
        if (!loading) {
            initMpe();
        }
    }, [loading]);

    const windowSku = window.epiProduct.sku;

    useEffect(() => {
        if (!windowSku) return;
        const parts = windowSku.split("\\");
        loadProduct({sku: parts[1], source: parts[0]});
    }, [windowSku]);

    if (loading) return (
        <BodySkeleton/>
    );

    return <>

        <div className="flex gap-2 mb-4">
            {d.regularPrice > d.salePrice && (
                <span className="bg-[#FE7C22] text-white text-[10px] font-bold px-2 py-1 rounded uppercase">
                    {Math.round((1 - d.salePrice / d.regularPrice) * 100)}% OFF
                </span>
            )}
            {d.freeShipping && (
                <span className="bg-dark-blue text-white text-[10px] font-bold px-2 py-1 rounded uppercase">
                    Free Shipping
                </span>
            )}
        </div>

        <div className="text-sm font-medium text-base-content/60 mb-1">
            {d.manufacturer}
        </div>

        <h1 className="!text-2xl md:!text-4xl font-bold text-base-content mb-4 leading-tight">{d.name}</h1>

        <div className="space-y-6 mb-8">
            {d.description && (
                <div
                    className="text-base text-base-content/70 leading-relaxed"
                    dangerouslySetInnerHTML={{__html: d.description}}
                />
            )}

            {Array.isArray(d.features) && d.features.length > 0 && (
                <div className="prose prose-sm max-w-full">
                    <ul className="list-disc pl-5 space-y-2 text-base-content/80">
                        {d.features.map((feature, index) => (
                            <li key={index} dangerouslySetInnerHTML={{__html: feature}}/>
                        ))}
                    </ul>
                </div>
            )}
        </div>

        <div className="flex items-center gap-2 mb-8">
            {d.customerReviewAverage >= 0 && (
                <RatingDisplay value={d.customerReviewAverage}/>
            )}
            {d.customerReviewCount >= 0 && (
                <span className="text-sm font-medium text-base-content/40">
                    ({d.customerReviewCount})
                </span>
            )}
        </div>

        <div className="mb-10">
            <div className="flex items-center gap-6 mb-2">
                <div className="flex flex-col">
                    <span className="text-5xl font-bold pt-dark-blue mpe-price">${d.salePrice}</span>
                    {d.regularPrice > d.salePrice && (
                        <span className="text-sm text-base-content/40 font-medium">
                            was <span className="line-through">${d.regularPrice}</span>
                        </span>
                    )}
                </div>

                <div className="h-10">
                    <SourceLogo source={d.source}/>
                </div>
            </div>

            <div className="pt-mpe mb-8"></div>

            <div className="max-w-xs space-y-4">
                <div className="flex items-center gap-4">
                    <div
                        className="flex items-center bg-base-100 border border-base-300 rounded-full overflow-hidden h-12">
                        <button
                            className="px-4 h-full hover:bg-base-200 transition-colors font-bold text-lg"
                            onClick={() => setQty(Math.max(1, qty - 1))}
                        >
                            −
                        </button>
                        <input
                            type="number"
                            min="1"
                            value={qty}
                            onChange={e => setQty(parseInt(e.target.value) || 1)}
                            className="w-12 text-center font-bold bg-transparent border-none focus:ring-0 text-base"
                        />
                        <button
                            className="px-4 h-full hover:bg-base-200 transition-colors font-bold text-lg"
                            onClick={() => setQty(qty + 1)}
                        >
                            +
                        </button>
                    </div>

                    <AddToCartButton
                        className="flex-1 border-none h-12 min-h-[3rem]"
                        sku={d.woocommerceId}
                        qty={qty}
                    />
                </div>

                <button
                    className={`btn btn-block h-12 min-h-[3rem] pt-btn-pill font-bold transition-all ${
                        inCompare ? 'btn-secondary' : 'btn-outline pt-orange border-current hover:bg-secondary/5'
                    }`}
                    onClick={handleToggleCompareStatus}
                >
                    <i className={`fa ${inCompare ? 'fa-check' : 'fa-exchange'} mr-2`}/>
                    {inCompare ? 'Selected' : 'Add to Compare'}
                </button>
            </div>
        </div>

    </>;
};